import { c as _c } from "react/compiler-runtime";
import { createContext, type ReactNode, useContext } from 'react';
import type { InlineToken } from '@/components/block-primitives';
import type { InvestigationSourceDetail } from '../../../hooks/use-sources';
import type { ChipState } from '../source-citation-shared';
import { isSourceToken, sourcesBySequence } from '../source-citation-shared';

// The looked-up source data. Citations are the only kind that resolves anything, which is why this isn't
// part of the shared inline-markup contract.
export type InlineResolutionResult = {
  source: InvestigationSourceDetail | undefined;
  state: ChipState;
};

// Block chips render via React inside an atom node view, out of the ProseMirror plugin's reach, so
// they read resolution from this context instead of the resolver a prose registry closes over.
export type InlineResolution = {
  resolve: (token: InlineToken) => InlineResolutionResult;
};

// No sources loaded (a block rendered outside the investigation editor, or before its sources land):
// every resolvable token reads as `missing` rather than throwing.
const EMPTY_RESOLUTION: InlineResolution = {
  resolve: () => ({
    source: undefined,
    state: 'missing'
  })
};
const InlineResolutionContext = createContext<InlineResolution>(EMPTY_RESOLUTION);
export const useInlineResolution = () => {
  return useContext(InlineResolutionContext);
};
export const InlineResolutionProvider = t0 => {
  const $ = _c(3);
  const {
    value,
    children
  } = t0;
  let t1;
  if ($[0] !== children || $[1] !== value) {
    t1 = <InlineResolutionContext.Provider value={value}>{children}</InlineResolutionContext.Provider>;
    $[0] = children;
    $[1] = value;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
};

// Mirrors the decoration path's state logic so a resolvable token inside a block and one in prose get
// the same missing / plain treatment from the one sources response.
export const buildInlineResolution = (sources: InvestigationSourceDetail[] = []): InlineResolution => {
  const bySequence = sourcesBySequence(sources);
  return {
    resolve: token => {
      if (!isSourceToken(token)) return {
        source: undefined,
        state: 'missing'
      };
      const source = bySequence[token.sequence];
      return source ? {
        source,
        state: 'plain'
      } : {
        source: undefined,
        state: 'missing'
      };
    }
  };
};